import { useEffect } from 'react';

/**
 * Add JSON-LD to the document head, and remove it again on unmount.
 *
 * Deliberately separate from usePageMeta rather than folded into it: that
 * function's save-and-restore bookkeeping is unit-tested and delicate, and
 * these blocks are simpler — we always create them and always remove them,
 * because nothing in public/index.html defines any to begin with.
 *
 * The head is outside #root, so writing here cannot upset hydration. It is
 * also captured by scripts/prerender.js, which snapshots the whole document,
 * so crawlers that never run JavaScript still get the markup.
 *
 * Exported separately from the hook so it can be tested without React.
 */
export function applyStructuredData(blocks) {
  if (!blocks || !blocks.length) return () => {};

  // Clear ours first. scripts/prerender.js snapshots the whole document, so
  // whatever was injected during prerendering is already baked into the HTML
  // this page was served as — appending without this leaves two copies of
  // every block in the DOM for anyone running JavaScript. The marker means we
  // only ever remove blocks we put there.
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-ld]')
    .forEach((el) => el.remove());

  const added = blocks.map((block) => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.ld = '';
    el.textContent = JSON.stringify(block);
    document.head.appendChild(el);
    return el;
  });

  return function restore() {
    added.forEach((el) => el.remove());
  };
}

/**
 * React binding. `blocks` is rebuilt on every render, so the effect keys off
 * the serialised form — passing the array itself would re-run on each render
 * and thrash the head.
 */
export default function useStructuredData(blocks) {
  const serialised = JSON.stringify(blocks || []);

  useEffect(() => {
    const parsed = JSON.parse(serialised);
    return applyStructuredData(Array.isArray(parsed) ? parsed : [parsed]);
  }, [serialised]);
}
